import '@theme/h17-dual-wiring';
import { CartErrorEvent, CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const H17_STORAGE_KEY = 'h17:selected-fitment:v2';
const H17_LEGACY_STORAGE_KEY = 'h17:selected-fitment:v1';
const H17_FIELDS = ['brand', 'year', 'modelFamily', 'packageTrim', 'engine'];
const H17_POSITION_FIELDS = { lower: 'lowerLight', upper: 'upperLight' };
const H17_POSITION_LABELS = { lower: 'Lower', upper: 'Upper' };
const H17_COMPONENT_LABELS = {
  lowerGrille: 'Lower grille',
  upperGrille: 'Upper grille'
};
const H17_COLLATOR = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

function h17Data() {
  return window.H17_FITMENT_DATA || { tree: {}, configurations: [], partIndex: {} };
}

function h17Key(fitment) {
  return JSON.stringify(H17_FIELDS.map((field) => String(fitment?.[field] || '')));
}

function h17FullLabel(fitment) {
  if (!fitment) return '';
  return [fitment.year, fitment.brand, fitment.modelFamily, fitment.packageTrim, fitment.engine]
    .filter(Boolean)
    .join(' · ');
}

function h17ShortLabel(fitment) {
  if (!fitment) return 'My Sled';
  return [fitment.year, fitment.brand, fitment.modelFamily].filter(Boolean).join(' ');
}

function h17ComponentGroupForRole(role) {
  if (role.startsWith('lowerGrille')) return 'lowerGrille';
  if (role.startsWith('upperGrille')) return 'upperGrille';
  return null;
}

function h17AggregateCompatibility(statuses) {
  const values = [...new Set(statuses.filter(Boolean))];
  if (!values.length) return 'notApplicable';
  return values.length === 1 ? values[0] : 'mixed';
}

function h17PartTypeLabel(part) {
  if (part?.bumperPart) return part.name;
  if (!part?.roles?.length) return 'OEM grille';
  return part.roles.map((role) => H17_COMPONENT_LABELS[role] || role).join(' / ');
}

function h17SetOptions(select, values, placeholder, selectedValue = '') {
  if (!select) return;
  selectedValue = String(selectedValue);
  select.replaceChildren(new Option(placeholder, ''));
  values.forEach((value) => select.add(new Option(value, value)));
  select.disabled = values.length === 0;
  select.value = values.includes(selectedValue) ? selectedValue : '';
}

class H17FitmentContext {
  constructor() {
    this.configurationByKey = new Map(
      (h17Data().configurations || []).map((fitment) => [h17Key(fitment), fitment])
    );
    this.dialog = document.querySelector('[data-h17-fitment-dialog]');
    this.form = this.dialog?.querySelector('[data-h17-fitment-form]');
    this.formStatus = this.dialog?.querySelector('[data-h17-fitment-form-status]');
    this.fields = Object.fromEntries(
      H17_FIELDS.map((field) => [field, this.form?.elements.namedItem(field)])
    );
    this.bumperClearance = '';
    this.selected = null;
    this.selectedPart = null;
    const restored = this.load();
    this.selected = restored.fitment;
    this.selectedPart = restored.part;
    this.bind();
    this.render();
  }

  load() {
    try {
      const saved = JSON.parse(
        localStorage.getItem(H17_STORAGE_KEY) || localStorage.getItem(H17_LEGACY_STORAGE_KEY) || 'null'
      );
      if (!saved) return { fitment: null, part: null };
      this.bumperClearance = ['clear', 'remove', 'blocked', 'unsure'].includes(saved.bumperClearance) ? saved.bumperClearance : ''; 
      if (saved.mode === 'part' && saved.partNumber) {
        return { fitment: null, part: this.partCompatibility(saved.partNumber) };
      }
      const fitment = this.configurationByKey.get(saved.key || h17Key(saved)) || null;
      return { fitment, part: null };
    } catch (error) {
      console.warn('[Hangar 17] Could not restore saved fitment.', error);
      return { fitment: null, part: null };
    }
  }

  bind() {
    document.addEventListener('change', (event) => {
      if (!event.target.matches('[data-h17-bumper-choice]')) return;
      this.bumperClearance = event.target.value;
      this.persist();
      this.render();
      window.dispatchEvent(new CustomEvent('h17:clearance-change'));
    });
    window.addEventListener('click', (event) => this.handlePurchaseClick(event), true);
    window.addEventListener('submit', (event) => this.handlePurchaseSubmit(event), true);

    document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
      event.promise
        ?.then(({ cart, detail }) => {
          const itemCount = cart?.totalQuantity ?? detail?.itemCount;
          if (Number.isFinite(itemCount)) this.updateHeaderCartCount(itemCount);
        })
        .catch((error) => {
          if (error?.name !== 'AbortError') console.warn('[Hangar 17] Could not update the header cart count.', error);
        });
    });

    document.addEventListener('click', (event) => {
      const openButton = event.target.closest('[data-h17-open-fitment]');
      if (openButton) {
        event.preventDefault();
        this.open();
        return;
      }

      const closeButton = event.target.closest('[data-h17-close-fitment]');
      if (closeButton) {
        event.preventDefault();
        this.close();
      }
    });

    this.dialog?.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.close();
    });

    this.fields.brand?.addEventListener('change', () => this.populateYears());
    this.fields.year?.addEventListener('change', () => this.populateFamilies());
    this.fields.modelFamily?.addEventListener('change', () => this.populateTrims());
    this.fields.packageTrim?.addEventListener('change', () => this.populateEngines());
    this.fields.engine?.addEventListener('change', () => this.validateForm());

    this.form?.addEventListener('submit', (event) => {
      event.preventDefault();
      const candidate = Object.fromEntries(H17_FIELDS.map((field) => [field, this.fields[field]?.value || '']));
      const fitment = this.configurationByKey.get(h17Key(candidate));
      if (!fitment) {
        this.formStatus.textContent = 'Select every field to save this sled.';
        return;
      }
      this.set(fitment);
      this.close();
    });

    this.dialog?.querySelector('[data-h17-clear-fitment]')?.addEventListener('click', () => {
      this.clear();
      this.close();
    });

    new MutationObserver(() => this.updateProductForms()).observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  open() {
    this.populateForm(this.selected);
    if (this.dialog?.showModal) this.dialog.showModal();
    else this.dialog?.setAttribute('open', '');
  }

  close() {
    if (this.dialog?.close) this.dialog.close();
    else this.dialog?.removeAttribute('open');
  }

  productGateForElement(element) {
    const banner = document.querySelector('[data-h17-product-compatibility]');
    const purchaseContainer = element?.closest?.('product-form-component, sticky-add-to-cart');
    if (!banner || !purchaseContainer) return null;
    if (String(purchaseContainer.dataset.productId || '') !== String(banner.dataset.h17ProductId || '')) return null;
    return { banner, position: banner.dataset.h17ProductPosition, purchaseContainer };
  }

  purchaseAllowed(position) {
    return this.isWholesaleOrder() || Boolean(this.hasSelection() && this.compatibility(position) === 'compatible');
  }

  isWholesaleOrder() {
    // Eligibility comes from the signed-in customer rendered by Liquid, never saved fitment data.
    return document.body.dataset.h17WholesaleApproved === 'true';
  }

  openPurchaseGate(position) {
    const positionLabel = H17_POSITION_LABELS[position] || 'selected';
    const currentState = this.hasSelection() ? this.compatibility(position) : 'none';
    this.open();
    if (!this.formStatus) return;
    this.formStatus.textContent = currentState === 'none'
      ? `Select your sled to confirm ${positionLabel} kit fitment before pre-ordering.`
      : `Your current fitment is not confirmed for the ${positionLabel} kit. Adjust the fields to continue.`;
  }

  handlePurchaseClick(event) {
    const buyButton = event.target.closest?.('[data-h17-buy-preorder]');
    const addButton = event.target.closest?.(
      'product-form-component [ref="addToCartButton"], sticky-add-to-cart [ref="addToCartButton"]'
    );
    const trigger = buyButton || addButton;
    if (!trigger) return;
    const gate = this.productGateForElement(trigger);
    if (!gate) return;

    if (!this.isWholesaleOrder() && !this.hasSelection()) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.openPurchaseGate(gate.position);
      return;
    }

    if (!this.purchaseAllowed(gate.position)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    if (buyButton) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.buyPreorderNow(buyButton, gate.position);
      return;
    }

    if (addButton.closest('sticky-add-to-cart')) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.addStickyPreorder(addButton, gate.position);
      return;
    }
    event.preventDefault();
    event.stopImmediatePropagation();
    this.addProductPreorder(addButton, gate.position);
  }

  handlePurchaseSubmit(event) {
    const gate = this.productGateForElement(event.target);
    if (!gate) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (!this.purchaseAllowed(gate.position)) {
      if (!this.hasSelection()) this.openPurchaseGate(gate.position);
      return;
    }
    this.addProductPreorder(event.submitter || event.target.querySelector('[type="submit"]'), gate.position);
  }

  async addProductPreorder(button, position) {
    const component = button?.closest('product-form-component');
    const form = component?.querySelector('form');
    if (!form?.reportValidity()) return;
    const variantId = Number(form.querySelector('[name="id"]')?.value || 0);
    const quantity = Number(form.querySelector('[name="quantity"]')?.value || component.dataset.quantityDefault || 1);
    if (!variantId || button.disabled) return;
    button.disabled = true;
    try { await this.addItems([{variantId,quantity,position}],button); }
    catch (error) {
      const status = component.querySelector('[data-h17-purchase-gate-note]');
      if (status) status.textContent = error.message || 'Unable to add this kit.';
    } finally { button.disabled = !this.purchaseAllowed(position); }
  }

  async addStickyPreorder(button, position) {
    const sticky = button.closest('sticky-add-to-cart');
    const variantId = Number(sticky?.dataset.currentVariantId || 0);
    const quantity = Number(sticky?.dataset.initialQuantity || 1);
    if (!variantId) return;
    button.disabled = true;
    try {
      await this.addItems([{ variantId, quantity, position }], button);
    } catch (error) {
      console.warn('[Hangar 17] Could not add the verified presale kit.', error);
    } finally {
      button.disabled = false;
    }
  }

  async buyPreorderNow(button, position) {
    const component = button.closest('product-form-component');
    const form = component?.querySelector('form');
    const variantId = Number(form?.querySelector('[name="id"]')?.value || 0);
    const quantity = Number(form?.querySelector('[name="quantity"]')?.value || component?.dataset.quantityDefault || 1);
    if (!variantId) return;
    const originalText = button.textContent;
    button.disabled = true;
    button.textContent = 'Preparing Pre-Order...';
    try {
      await this.addItems([{ variantId, quantity, position }], button, { openDrawer: false });
      if (this.isWholesaleOrder()) window.location.assign(`${window.Shopify?.routes?.root || '/'}checkout`);
      else await window.H17DualWiring.checkout();
      button.disabled = false;
      button.textContent = originalText;
    } catch (error) {
      const status = component?.querySelector('[data-h17-purchase-gate-note]');
      if (status) status.textContent = error.message || 'Unable to prepare checkout. Please try again.';
      button.disabled = false;
      button.textContent = originalText;
    }
  }

  populateForm(selection = null) {
    const brands = Object.keys(h17Data().tree || {}).sort(H17_COLLATOR.compare);
    h17SetOptions(this.fields.brand, brands, 'Select brand', selection?.brand || '');
    this.populateYears(selection);
  }

  populateYears(selection = null) {
    const years = Object.keys(h17Data().tree?.[this.fields.brand?.value] || {}).sort((a, b) => Number(b) - Number(a));
    h17SetOptions(this.fields.year, years, this.fields.brand?.value ? 'Select year' : 'Select brand first', selection?.year || '');
    this.populateFamilies(selection);
  }

  populateFamilies(selection = null) {
    const families = Object.keys(
      h17Data().tree?.[this.fields.brand?.value]?.[this.fields.year?.value] || {}
    ).sort(H17_COLLATOR.compare);
    h17SetOptions(this.fields.modelFamily, families, this.fields.year?.value ? 'Select model family' : 'Select year first', selection?.modelFamily || '');
    this.populateTrims(selection);
  }

  populateTrims(selection = null) {
    const trims = Object.keys(
      h17Data().tree?.[this.fields.brand?.value]?.[this.fields.year?.value]?.[this.fields.modelFamily?.value] || {}
    ).sort(H17_COLLATOR.compare);
    h17SetOptions(this.fields.packageTrim, trims, this.fields.modelFamily?.value ? 'Select package/trim' : 'Select model family first', selection?.packageTrim || '');
    this.populateEngines(selection);
  }

  populateEngines(selection = null) {
    const engines = (
      h17Data().tree?.[this.fields.brand?.value]?.[this.fields.year?.value]?.[this.fields.modelFamily?.value]?.[this.fields.packageTrim?.value] || []
    ).slice().sort(H17_COLLATOR.compare);
    h17SetOptions(this.fields.engine, engines, this.fields.packageTrim?.value ? 'Select engine' : 'Select package/trim first', selection?.engine || '');
    this.validateForm();
  }

  validateForm() {
    const complete = H17_FIELDS.every((field) => this.fields[field]?.value);
    const saveButton = this.form?.querySelector('[data-h17-save-fitment]');
    if (saveButton) saveButton.disabled = !complete;
    if (this.formStatus) this.formStatus.textContent = complete ? 'Ready to save.' : 'Complete all five fields.';
  }

  partCompatibility(partNumber) {
    if (window.H17FitmentRules) return window.H17FitmentRules.part(partNumber);
    const normalized = String(partNumber || '').match(/\d{6,}/)?.[0];
    if (!normalized || !(h17Data().partIndex?.[normalized] || []).length) return null;
    const roles = new Set();
    const lowerStatuses = [];
    const upperStatuses = [];

    h17Data().partIndex[normalized].forEach((index) => {
      const fitment = h17Data().configurations?.[index];
      if (!fitment) return;
      Object.entries(fitment.components || {}).forEach(([role, numbers]) => {
        if (!numbers.includes(normalized)) return;
        const group = h17ComponentGroupForRole(role);
        if (!group) return;
        roles.add(group);
        const componentStatus = fitment.componentCompatibility?.[role]?.[normalized];
        if (group === 'lowerGrille') {
          lowerStatuses.push(componentStatus || fitment.compatibility?.lowerLight || 'unknown');
        } else if (group === 'upperGrille') {
          upperStatuses.push(componentStatus || fitment.compatibility?.upperLight || 'unknown');
        } else {
          lowerStatuses.push(fitment.compatibility?.lowerLight || 'unknown');
          upperStatuses.push(fitment.compatibility?.upperLight || 'unknown');
        }
      });
    });

    const compatibility = {
      lowerLight: h17AggregateCompatibility(lowerStatuses),
      upperLight: h17AggregateCompatibility(upperStatuses)
    };

    return {
      partNumber: normalized,
      roles: [...roles],
      compatibility,
      ambiguous: Object.values(compatibility).includes('mixed')
    };
  }

  set(fitment) {
    const verified = this.configurationByKey.get(h17Key(fitment));
    if (!verified) return false;
    if (h17Key(this.selected) !== h17Key(verified) || this.selectedPart) this.bumperClearance = '';
    this.selected = verified;
    this.selectedPart = null;
    this.persist();
    localStorage.removeItem(H17_LEGACY_STORAGE_KEY);
    this.render();
    window.dispatchEvent(new CustomEvent('h17:fitment-change', { detail: { fitment: verified, part: null } }));
    return true;
  }

  setPart(partNumber) {
    const verified = this.partCompatibility(partNumber);
    if (!verified) return false;
    if (this.selectedPart?.partNumber !== verified.partNumber || this.selected) this.bumperClearance = '';
    this.selected = null;
    this.selectedPart = verified;
    this.persist();
    localStorage.removeItem(H17_LEGACY_STORAGE_KEY);
    this.render();
    window.dispatchEvent(new CustomEvent('h17:fitment-change', { detail: { fitment: null, part: verified } }));
    return true;
  }

  persist() {
    const value = this.selected ? {mode:'vehicle',key:h17Key(this.selected)} : {mode:'part',partNumber:this.selectedPart?.partNumber};
    value.bumperClearance = this.bumperClearance;
    localStorage.setItem(H17_STORAGE_KEY, JSON.stringify(value));
  }

  reason(position) {
    return window.H17FitmentRules?.reason(this.selected || this.selectedPart, position, this.bumperClearance) || '';
  }

  renderBumperChecks() {
    const record = this.selected || this.selectedPart;
    const reason = this.reason('lower');
    document.querySelectorAll('[data-h17-bumper-check]').forEach((panel) => {
      const needed = Boolean(record && (record.obstructions?.lowerLight || record.bumperPart || window.H17FitmentRules?.needsCheck(record)));
      const isFitmentGuide = panel.closest('kit-finder')?.closest('[data-wholesale-ordering-view]')?.dataset.wholesaleOrderingView === 'false';
      panel.hidden = !needed || (this.isWholesaleOrder() && !isFitmentGuide);
      panel.querySelector('[data-h17-bumper-reason]').textContent = reason || 'Your lower grille must remain unobstructed for the Lower Spot Pro kit to fit.';
      panel.querySelector('[data-h17-bumper-field]').hidden = Boolean(record?.bumperPart);
      panel.querySelector('[data-h17-bumper-choice]').value = this.bumperClearance;
      const graphic = panel.querySelector('[data-h17-bumper-graphic]');
      if (graphic) {
        // The illustration depicts Explorer², not every unverified accessory bumper.
        graphic.hidden = !Boolean(record?.obstructions?.lowerLight || record?.bumperCheck ||
          (record?.bumperPart && ['860202714', '860202915'].includes(record.partNumber)));
      }
    });
  }

  clear() {
    this.bumperClearance = '';
    this.selected = null;
    this.selectedPart = null;
    localStorage.removeItem(H17_STORAGE_KEY);
    localStorage.removeItem(H17_LEGACY_STORAGE_KEY);
    this.render();
    window.dispatchEvent(new CustomEvent('h17:fitment-change', { detail: { fitment: null, part: null } }));
  }

  hasSelection() {
    return Boolean(this.selected || this.selectedPart);
  }

  compatibility(position) {
    if (this.selectedPart?.ambiguous) return 'mixed';
    if (window.H17FitmentRules) return window.H17FitmentRules.status(this.selected || this.selectedPart, position, this.bumperClearance);
    const source = this.selected?.compatibility || this.selectedPart?.compatibility;
    return source?.[H17_POSITION_FIELDS[position]] || 'unknown';
  }

  lineProperties(position, selection = null) {
    const selected = selection ? selection.fitment : this.selected;
    const selectedPart = selection ? null : this.selectedPart;
    const bumperClearance = selection ? selection.clearance : this.bumperClearance;
    const verification = selection
      ? window.H17FitmentRules?.status(selected, position, bumperClearance) || selected?.compatibility?.[H17_POSITION_FIELDS[position]] || 'unknown'
      : this.compatibility(position);
    const properties = {
      'Order type': 'Presale',
      'Estimated shipping': 'December 2026–January 14, 2027 (estimate; inventory expected December)'
    };
    if (this.isWholesaleOrder()) {
      // Stock orders are not tied to a browser's saved personal sled or a compatibility claim.
      properties['Ordering account'] = 'Wholesale';
      return properties;
    }
    if (selected) {
      Object.assign(properties, {
        'Selected sled': h17FullLabel(selected),
        'Fitment position': `${H17_POSITION_LABELS[position]} installation`,
        'Fitment verification': verification,
        '_H17 fitment key': h17Key(selected)
      });
    } else if (selectedPart) {
      Object.assign(properties, {
        'OEM grille lookup': selectedPart.partNumber,
        'OEM grille type': h17PartTypeLabel(selectedPart),
        'Fitment position': `${H17_POSITION_LABELS[position]} installation`,
        'Fitment verification': verification,
        '_H17 fitment lookup mode': 'OEM grille part number'
      });
    }
    if (position === 'lower' && bumperClearance === 'clear') properties['Front bumper clearance'] = 'Customer confirmed lower grille is unobstructed';
    if (position === 'lower' && bumperClearance === 'remove') properties['Front bumper clearance'] = 'Customer has removed or will remove the obstruction before installing the lower kit';
    return properties;
  }

  render() {
    const hasSelection = this.hasSelection();
    document.querySelectorAll('[data-h17-my-sled]').forEach((button) => {
      button.dataset.hasFitment = hasSelection ? 'true' : 'false';
      const label = button.querySelector('[data-h17-my-sled-label]');
      const detail = button.querySelector('[data-h17-my-sled-detail]');
      if (label) label.textContent = this.selected ? 'My Sled' : this.selectedPart ? 'My Fitment' : 'Select My Sled';
      if (detail) detail.textContent = this.selected
        ? h17ShortLabel(this.selected)
        : this.selectedPart
          ? `OEM ${this.selectedPart.partNumber}`
          : 'Check compatibility';
      const ariaLabel = this.selected
        ? `My Sled: ${h17FullLabel(this.selected)}. Adjust fitment.`
        : this.selectedPart
          ? `My Fitment: OEM grille ${this.selectedPart.partNumber}, ${h17PartTypeLabel(this.selectedPart)}. Adjust fitment.`
          : 'Select your sled or search by OEM grille number';
      button.setAttribute('aria-label', ariaLabel);
    });

    const currentPart = this.dialog?.querySelector('[data-h17-current-part]');
    if (currentPart) {
      currentPart.hidden = !this.selectedPart;
      currentPart.textContent = this.selectedPart
        ? `Current OEM grille lookup: ${this.selectedPart.partNumber} · ${h17PartTypeLabel(this.selectedPart)}`
        : '';
    }
    this.renderBumperChecks();
    this.renderProductCompatibility();
    this.updateProductForms();
  }

  renderProductCompatibility() {
    document.querySelectorAll('[data-h17-product-compatibility]').forEach((banner) => {
      const position = banner.dataset.h17ProductPosition;
      const state = this.hasSelection() ? this.compatibility(position) : 'none';
      const status = banner.querySelector('[data-h17-product-status]');
      const selection = banner.querySelector('[data-h17-product-selected-sled]');
      const fitmentGroup = banner.closest('[data-h17-product-fitment-group]');
      const interest = banner.querySelector('[data-h17-compatible-interest]');
      const interestPrefix = banner.querySelector('[data-h17-compatible-interest-prefix]');
      const interestKit = banner.querySelector('[data-h17-compatible-interest-kit]');
      const alternative = fitmentGroup?.querySelector('[data-h17-compatible-alternative]');
      if (this.isWholesaleOrder()) {
        banner.dataset.state = 'wholesale';
        banner.hidden = false;
        status.textContent = 'Wholesale ordering';
        selection.textContent = 'No sled selection is required for your wholesale order. Use the fitment guides when matching a kit to a customer’s sled.';
        if (interest) interest.hidden = true;
        if (alternative) alternative.hidden = true;
        return;
      }
      banner.dataset.state = state;
      banner.hidden = false;

      if (!this.hasSelection()) {
        status.textContent = 'Select your sled or OEM grille number to check compatibility.';
        selection.textContent = 'Your selection will stay with you as you browse the site.';
        if (interest) interest.hidden = true;
        if (alternative) alternative.hidden = true;
        return;
      }

      selection.textContent = this.selected
        ? h17FullLabel(this.selected)
        : this.selectedPart.bumperPart ? `${this.selectedPart.name} · ${this.selectedPart.partNumber}` : `OEM grille ${this.selectedPart.partNumber} · ${h17PartTypeLabel(this.selectedPart)}`;
      if (state === 'compatible') {
        status.textContent = this.selectedPart
          ? `This light is compatible with OEM grille ${this.selectedPart.partNumber}.`
          : 'This light is compatible with your sled.';
        const otherPosition = position === 'lower' ? 'upper' : 'lower';
        const otherCompatible = this.compatibility(otherPosition) === 'compatible';
        if (interest) interest.hidden = !otherCompatible;
        if (alternative) alternative.hidden = !otherCompatible;
        if (otherCompatible) {
          if (interestPrefix) {
            interestPrefix.textContent = this.selectedPart
              ? 'This OEM grille is also compatible with the'
              : 'This sled is also compatible with the';
          }
          if (interestKit) interestKit.textContent = `${H17_POSITION_LABELS[otherPosition]} Spot Pro Kit.`;
          if (alternative) alternative.textContent = `View the ${H17_POSITION_LABELS[otherPosition]} Spot Pro Kit`;
        }
      } else if (state === 'incompatible' || state === 'notApplicable') {
        status.textContent = (this.reason(position) ? 'Not compatible — front bumper obstruction.' : '') || (this.selectedPart
          ? `This light is NOT compatible with OEM grille ${this.selectedPart.partNumber}.`
          : 'This light is NOT compatible with your sled.');
        const otherPosition = position === 'lower' ? 'upper' : 'lower';
        const otherCompatible = this.compatibility(otherPosition) === 'compatible';
        if (interest) interest.hidden = !otherCompatible;
        if (alternative) alternative.hidden = !otherCompatible;
        if (otherCompatible) {
          if (interestPrefix) {
            interestPrefix.textContent = this.selectedPart
              ? 'This OEM grille is compatible with the'
              : 'This sled is compatible with the';
          }
          if (interestKit) interestKit.textContent = `${H17_POSITION_LABELS[otherPosition]} Spot Pro Kit.`;
          if (alternative) alternative.textContent = `View the compatible ${H17_POSITION_LABELS[otherPosition]} Spot Pro Kit`;
        }
      } else {
        status.textContent = this.reason(position) || (this.selectedPart
          ? `Compatibility for OEM grille ${this.selectedPart.partNumber} varies by sled configuration.`
          : 'Confirm your installed lower or upper OEM grille number before ordering.');
        if (interest) interest.hidden = true;
        if (alternative) alternative.hidden = true;
      }
    });
  }

  updateProductForms() {
    const banner = document.querySelector('[data-h17-product-compatibility]');
    if (!banner) return;
    const position = banner.dataset.h17ProductPosition;
    const productId = banner.dataset.h17ProductId;
    const hasSelection = this.hasSelection();
    const allowed = this.purchaseAllowed(position);
    const gateState = this.isWholesaleOrder() ? 'wholesale' : allowed ? 'confirmed' : hasSelection ? 'blocked' : 'required';
    const formComponents = productId
      ? document.querySelectorAll(`product-form-component[data-product-id="${CSS.escape(productId)}"]`)
      : [];
    formComponents.forEach((component) => {
      component.dataset.h17FitmentState = gateState;
      component.toggleAttribute('data-h17-fitment-blocked', gateState === 'blocked');
      const note = component.querySelector('[data-h17-purchase-gate-note]');
      if (!note) return;
      note.dataset.state = gateState;
      const noteText = this.isWholesaleOrder()
        ? 'No sled selection is required for wholesale ordering.'
        : allowed
          ? 'Fitment confirmed. This kit is compatible with your selection.'
          : hasSelection
            ? this.reason(position) || 'This kit is not compatible with your selected fitment. Use Adjust Fitment above to choose another sled.'
            : 'Confirm a compatible fitment to unlock both pre-order buttons.';
      if (note.textContent !== noteText) note.textContent = noteText;
    });

    formComponents.forEach((component) => {
      const form = component.querySelector('form');
      if (!form) return;
      const properties = this.lineProperties(position);
      const expectedNames = new Set(Object.keys(properties).map((name) => `properties[${name}]`));
      form.querySelectorAll('[data-h17-fitment-property]').forEach((input) => {
        if (!expectedNames.has(input.name)) input.remove();
      });
      Object.entries(properties).forEach(([name, value]) => {
        const inputName = `properties[${name}]`;
        let input = [...form.querySelectorAll('[data-h17-fitment-property]')]
          .find((candidate) => candidate.name === inputName);
        if (!input) {
          input = document.createElement('input');
          input.type = 'hidden';
          input.name = inputName;
          input.dataset.h17FitmentProperty = 'true';
          form.append(input);
        }
        input.value = value;
      });
    });

    const stickyComponents = productId
      ? document.querySelectorAll(`sticky-add-to-cart[data-product-id="${CSS.escape(productId)}"]`)
      : [];
    stickyComponents.forEach((component) => {
      component.dataset.h17FitmentState = gateState;
    });

    const purchaseButtons = [
      ...[...formComponents].flatMap((component) => [...component.querySelectorAll('[ref="addToCartButton"]')]),
      ...[...formComponents].flatMap((component) => [...component.querySelectorAll('[data-h17-buy-preorder]')]),
      ...[...stickyComponents].flatMap((component) => [...component.querySelectorAll('[ref="addToCartButton"]')])
    ];
    purchaseButtons.forEach((button) => {
      const wasDisabledByFitment = button.dataset.h17FitmentDisabled === 'true';
      if (gateState === 'blocked') {
        if (!wasDisabledByFitment) {
          button.dataset.h17DisabledBeforeFitment = button.disabled ? 'true' : 'false';
        }
        button.disabled = true;
        button.dataset.h17FitmentDisabled = 'true';
        button.setAttribute('aria-disabled', 'true');
      } else if (wasDisabledByFitment) {
        if (button.dataset.h17DisabledBeforeFitment !== 'true') {
          button.disabled = false;
          button.removeAttribute('aria-disabled');
        }
        delete button.dataset.h17FitmentDisabled;
        delete button.dataset.h17DisabledBeforeFitment;
      }

      if (gateState === 'required') {
        button.setAttribute('aria-haspopup', 'dialog');
        button.setAttribute('aria-controls', 'H17FitmentDialog');
      } else {
        button.removeAttribute('aria-haspopup');
        button.removeAttribute('aria-controls');
      }
    });
  }

  async addItems(items, trigger = document.body, options = {}) {
    if (!this.isWholesaleOrder() && !this.hasSelection()) throw new Error('Select your sled or OEM grille number before adding a fitment kit.');
    const invalidItem = items.find((item) => !this.purchaseAllowed(item.position));
    if (invalidItem) throw new Error(`The ${invalidItem.position} kit is not confirmed for your selected fitment.`);

    const prepared = await window.H17DualWiring.prepare(items, this);
    items = prepared.items;
    if (!items.length) {
      const cart = prepared.cart || await window.H17DualWiring.cart();
      this.updateHeaderCartCount(cart.item_count);
      if (options.openDrawer !== false) window.location.assign(`${window.Shopify?.routes?.root || '/'}cart`);
      return {...cart,h17AddedPositions:[]};
    }
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionIds = [...cartItemsComponents]
      .map((component) => component instanceof HTMLElement ? component.dataset.sectionId : '')
      .filter(Boolean);
    const deferred = CartLinesUpdateEvent.createPromise();
    const lines = items.map((item) => ({
      merchandiseId: String(item.variantId),
      quantity: Math.max(1, Number(item.quantity) || 1)
    })).concat((prepared.accessoryItems || []).map(item => ({merchandiseId:String(item.id),quantity:item.quantity})));
    trigger.dispatchEvent(new CartLinesUpdateEvent({ action: 'add', context: 'product', lines, promise: deferred.promise }));

    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: items.map((item) => ({
            id: Number(item.variantId),
            quantity: Math.max(1, Number(item.quantity) || 1),
            properties: {...(item.properties || this.lineProperties(item.position)),...prepared.properties}
          })).concat(prepared.accessoryItems || []),
          sections: sectionIds.join(',')
        })
      });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || result.message || 'Unable to add this kit.');
      const cart = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`).then((cartResponse) => cartResponse.json());
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, sections: result.sections, didError: false, source: 'h17-fitment-finder' }
      });
      this.updateHeaderCartCount(cart.item_count);
      const drawer = document.getElementById('cart-drawer');
      if (options.openDrawer !== false && drawer?.open) drawer.open();
      return {...cart,h17AddedPositions:items.map(item => item.position)};
    } catch (error) {
      deferred.reject(error);
      trigger.dispatchEvent(new CartErrorEvent({ error: error.message || 'Unable to add this kit.', code: 'INVALID' }));
      throw error;
    }
  }

  updateHeaderCartCount(count) {
    document.querySelectorAll('.h17-site-header__cart').forEach((link) => {
      link.setAttribute('aria-label', `Cart with ${count} items`);
      let badge = link.querySelector('span');
      if (count > 0 && !badge) {
        badge = document.createElement('span');
        link.append(badge);
      }
      if (badge) {
        badge.textContent = String(count);
        badge.hidden = count === 0;
      }
    });
  }
}

window.H17Fitment = new H17FitmentContext();
window.dispatchEvent(new CustomEvent('h17:fitment-ready', {
  detail: { fitment: window.H17Fitment.selected, part: window.H17Fitment.selectedPart }
}));
